import { useQuery, useQueryClient } from "@tanstack/react-query";
import { userService } from "@/services/userService";
import { authLogger } from "@/utils/logger";

interface CurrentUser {
  id: string;
  name: string;
  email: string;
  phone?: string;
  avatar?: string;
  company?: string;
  companyId?: string;
  schemaName?: string; // Adicionado para multi-tenant websocket
  roles: string[];
}

export const useCurrentUser = () => {
  const queryClient = useQueryClient();

  // Buscar dados reais do usuário logado
  const {
    data: userData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["currentUser"],
    queryFn: () => userService.getCurrentUser(),
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos no cache
    retry: 1,
  });

  // Transformar dados do backend para o formato esperado
  const currentUser: CurrentUser = userData
    ? {
        id: userData.id,
        name: userData.displayName || userData.email.split("@")[0],
        email: userData.email,
        company: userData.company?.name || "Empresa",
        companyId: userData.company?.id,
        schemaName: userData.company?.schemaName, // Expor schemaName
        // Garantir que roles seja sempre um array, mesmo se vier como string ou null
        roles: Array.isArray(userData.role)
          ? userData.role
          : userData.role
          ? [userData.role]
          : [],
        avatar: "", // Adicionar quando tiver avatar no backend
      }
    : {
        // Fallback enquanto carrega
        id: "",
        name: "Carregando...",
        email: "",
        company: "Sistema",
        roles: [],
      };

  // Adicionar log para depuração
  if (userData && process.env.NODE_ENV === "development") {
    authLogger.info("🔍 Dados do usuário recebidos:", userData);
    authLogger.info("🔍 Dados do usuário processados:", currentUser);
  }

  const updateUser = (updatedData: Partial<CurrentUser>) => {
    // Atualizar os dados do usuário no cache do React Query
    if (userData) {
      const updatedUserData = {
        ...userData,
        displayName: updatedData.name || userData.displayName,
        email: updatedData.email || userData.email,
        // Outros campos podem ser adicionados conforme necessário
      };

      // Atualizar o cache
      queryClient.setQueryData(["currentUser"], updatedUserData);

      authLogger.info("Dados do usuário atualizados:", updatedUserData);
    } else {
      authLogger.warn(
        "Tentativa de atualizar dados do usuário sem dados iniciais"
      );
    }
  };

  return {
    currentUser,
    updateUser,
    isLoading,
    error,
  };
};
